import { clearNotificationStores } from './useNotifications';
import { useState, useEffect, useSyncExternalStore } from 'react';
import { apiFetch } from '../utils/api';
import { changeSession, clearLegacySession, sessionState } from '../utils/sessionState';

let snapshot = { user: null, loading: true, error: null };
let restoring;
const listeners = new Set();
const subscribe = listener => { listeners.add(listener); return () => listeners.delete(listener); };
const getSnapshot = () => snapshot;
function update(next) { snapshot = { ...snapshot, ...next }; listeners.forEach(listener => listener()); }
function saveUser(_unusedToken, userData) {
  clearLegacySession(); changeSession(true);
  update({ user: userData, loading: false, error: null });
}
async function restoreSession() {
  if (restoring) return restoring;
  const revision = sessionState().revision;
  update({ loading: !snapshot.user, error: null });
  restoring = (async () => {
    // Exchange an existing browser token once; never persist it again.
    const legacy = localStorage.getItem('scheduler_token') || sessionStorage.getItem('scheduler_token');
    const rememberMe = !!localStorage.getItem('scheduler_token');
    clearLegacySession();
    const result = legacy ? await apiFetch('/api/auth/session', { method: 'POST', headers: { Authorization: `Bearer ${legacy}` }, body: JSON.stringify({ rememberMe }) }) : await apiFetch('/api/auth/me');
    if (revision !== sessionState().revision) return;
    if (result.ok) saveUser(null, result.user);
    else if (result.message === 'Not logged in.' || result.message?.startsWith('Session expired')) {
      changeSession(false); update({ user: null, loading: false, error: null });
    } else update({ loading: false, error: result.message || 'Could not check your sign-in. Please try again.' });
  })().finally(() => { restoring = null; });
  return restoring;
}
async function logout() {
  const result = await apiFetch('/api/auth/logout', { method: 'POST', body: '{}' });
  if (!result.ok) throw new Error(result.message || 'Could not sign out. Please try again.');
  clearNotificationStores(); clearLegacySession(); changeSession(false);
  localStorage.removeItem('scheduler_session_expired');
  update({ user: null, loading: false, error: null });
}
window.addEventListener('scheduler-session-expired', () => {
  clearNotificationStores(); clearLegacySession(); changeSession(false);
  localStorage.setItem('scheduler_session_expired', '1');
  update({ user: null, loading: false, error: null });
});
window.addEventListener('scheduler-mfa-required', () => { restoreSession(); });

export function useAuth() {
  const state = useSyncExternalStore(subscribe, getSnapshot);
  const [theme, setTheme] = useState(() => localStorage.getItem('scheduler_theme') || 'light');
  useEffect(() => { if (snapshot.loading) restoreSession(); }, []);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('scheduler_theme', theme);
  }, [theme]);
  return { ...state, saveUser, logout, refreshSession: restoreSession, theme, toggleTheme: () => setTheme(prev => prev === 'light' ? 'dark' : 'light') };
}
